<div class="error-logs-page">
  <div class="page-header">
    <h1>Job Error Logs</h1>
    <p>Monitor, search, and troubleshoot processing errors across all jobs</p>
  </div>

  <div class="filters-card">
    <div class="filters-grid">
      <div class="filter-group">
        <label for="jobId">Job ID</label>
        <input
          id="jobId"
          type="number"
          [(ngModel)]="jobId"
          (ngModelChange)="onFilterChange()"
          placeholder="Filter by Job ID..."
        />
      </div>

      <div class="filter-group">
        <label for="jobImageId">Job Image ID</label>
        <input
          id="jobImageId"
          type="number"
          [(ngModel)]="jobImageId"
          (ngModelChange)="onFilterChange()"
          placeholder="Filter by Image ID..."
        />
      </div>

      <div class="filter-group">
        <label for="processType">Process Type</label>
        <input
          id="processType"
          type="text"
          [(ngModel)]="processType"
          (ngModelChange)="onFilterChange()"
          placeholder="e.g. Upload, Crop..."
        />
      </div>

      <div class="filter-group">
        <label for="stage">Stage</label>
        <input
          id="stage"
          type="text"
          [(ngModel)]="stage"
          (ngModelChange)="onFilterChange()"
          placeholder="e.g. Preparing, Processing..."
        />
      </div>

      <div class="filter-group">
        <label for="status">Status</label>
        <input
          id="status"
          type="text"
          [(ngModel)]="status"
          (ngModelChange)="onFilterChange()"
          placeholder="e.g. Failed, Requeued..."
        />
      </div>

      <div class="filter-group">
        <label for="errorCode">Error Code</label>
        <input
          id="errorCode"
          type="text"
          [(ngModel)]="errorCode"
          (ngModelChange)="onFilterChange()"
          placeholder="Filter by error code..."
        />
      </div>

      <div class="filter-group">
        <label for="sourceWorker">Source Worker</label>
        <input
          id="sourceWorker"
          type="text"
          [(ngModel)]="sourceWorker"
          (ngModelChange)="onFilterChange()"
          placeholder="Filter by worker name..."
        />
      </div>

      <div class="filter-group actions-group">
        <label class="invisible-label">&nbsp;</label>
        <button type="button" class="reset-btn" (click)="resetFilters()">
          ↺ Reset Filters
        </button>
      </div>
    </div>
  </div>

  @if (isLoading && items.length === 0) {
    <div class="empty-state">Loading error logs...</div>
  } @else if (errorMessage && items.length === 0) {
    <div class="empty-state error">{{ errorMessage }}</div>
  } @else {
    <div class="reports-card">
      <div class="table-scroll">
        <table class="reports-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Job ID</th>
              <th>Image ID</th>
              <th>Process Type</th>
              <th>Stage</th>
              <th>Status</th>
              <th>Error Code</th>
              <th>Message</th>
              <th>Occurred At</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>

          <tbody>
            @for (log of items; track log.jobErrorLogId) {
              <tr>
                <td class="log-id-cell">#{{ log.jobErrorLogId }}</td>
                <td>{{ log.jobId }}</td>
                <td>{{ log.jobImageId || '-' }}</td>
                <td>
                  <span class="process-badge">{{ log.processType || '-' }}</span>
                </td>
                <td>{{ log.stage || '-' }}</td>
                <td>
                  <span class="status-badge" [class.badge-failed]="log.status?.toLowerCase() === 'failed'" [class.badge-requeued]="log.status?.toLowerCase() === 'requeued'">
                    {{ log.status || '-' }}
                  </span>
                </td>
                <td>
                  <code class="error-code">{{ log.errorCode || '-' }}</code>
                </td>
                <td class="message-cell" [title]="log.errorMessage || ''">
                  {{ getShortErrorMessage(log.errorMessage) }}
                </td>
                <td class="time-cell">{{ formatDate(log.occurredAt) }}</td>
                <td class="text-right">
                  <button type="button" class="view-details-btn" (click)="openDetails(log)">
                    View Details
                  </button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      @if (items.length === 0 && !isLoading) {
        <div class="empty-state">No matching error logs found.</div>
      }

      @if (totalPages > 1) {
        <div class="pagination-bar">
          <button
            type="button"
            class="page-btn"
            [disabled]="pageNumber === 1"
            (click)="goToPrevPage()"
          >
            ← Previous
          </button>

          <div class="page-info">
            Page <strong>{{ pageNumber }}</strong> of <strong>{{ totalPages }}</strong>
            &nbsp;·&nbsp; {{ startIndex }}–{{ endIndex }} of {{ totalCount }}
          </div>

          <button
            type="button"
            class="page-btn"
            [disabled]="pageNumber === totalPages"
            (click)="goToNextPage()"
          >
            Next →
          </button>
        </div>
      }
    </div>
  }

  <!-- Details Overlay Modal -->
  @if (selectedLog) {
    <div class="modal-overlay" (click)="closeDetails()">
      <div class="details-modal" (click)="$event.stopPropagation()">
        <div class="modal-header">
          <h3>Error Log Detail #{{ selectedLog.jobErrorLogId }}</h3>
          <button type="button" class="close-modal-btn" (click)="closeDetails()">×</button>
        </div>

        <div class="modal-body">
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">Job ID</span>
              <span class="detail-val">{{ selectedLog.jobId }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Image ID</span>
              <span class="detail-val">{{ selectedLog.jobImageId || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Process ID</span>
              <span class="detail-val">{{ selectedLog.jobImageProcessId || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Process Type</span>
              <span class="detail-val">{{ selectedLog.processType || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Process Sub-Type</span>
              <span class="detail-val">{{ selectedLog.processSubType || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Stage</span>
              <span class="detail-val">{{ selectedLog.stage || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Status</span>
              <span class="detail-val">{{ selectedLog.status || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Error Code</span>
              <span class="detail-val"><code>{{ selectedLog.errorCode || '-' }}</code></span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Attempts</span>
              <span class="detail-val">{{ selectedLog.attemptCount }} / {{ selectedLog.maxRetryCount }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Source Worker</span>
              <span class="detail-val">{{ selectedLog.sourceWorker || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Queue Name</span>
              <span class="detail-val">{{ selectedLog.queueName || '-' }}</span>
            </div>
            <div class="detail-item full-width">
              <span class="detail-label">Blob Path</span>
              <span class="detail-val select-all">
                @if (isUrl(selectedLog.blobPath)) {
                  <a [href]="decodeUrl(selectedLog.blobPath)" target="_blank" class="decoded-link">
                    {{ decodeUrl(selectedLog.blobPath) }}
                  </a>
                } @else {
                  <code>{{ decodeUrl(selectedLog.blobPath) || '-' }}</code>
                }
              </span>
            </div>
            <div class="detail-item full-width">
              <span class="detail-label">Error Message & Service Details</span>
              
              @if (parseErrorMessage(selectedLog.errorMessage); as parsedMsg) {
                <div class="parsed-message-container">
                  
                  <!-- Intro status bar -->
                  @if (parsedMsg.intro) {
                    <div class="error-intro-box">
                      {{ parsedMsg.intro }}
                    </div>
                  }

                  <!-- Cleaned SAS URLs Section -->
                  @if (parsedMsg.imageUrls.length > 0 || parsedMsg.referenceImageUrls.length > 0) {
                    <div class="extracted-urls-card">
                      <h4 class="card-subtitle">Extracted SAS Media Links</h4>
                      
                      @for (url of parsedMsg.imageUrls; track url) {
                        <div class="media-link-group">
                          <span class="url-badge badge-primary">Original Image URL</span>
                          <a [href]="decodeUrl(url)" target="_blank" class="decoded-link media-link">
                            {{ decodeUrl(url) }}
                          </a>
                        </div>
                      }

                      @for (url of parsedMsg.referenceImageUrls; track url) {
                        <div class="media-link-group">
                          <span class="url-badge badge-secondary">Reference Crop URL</span>
                          <a [href]="decodeUrl(url)" target="_blank" class="decoded-link media-link">
                            {{ decodeUrl(url) }}
                          </a>
                        </div>
                      }
                    </div>
                  }

                  <!-- Separate Boxes for Request and Response -->
                  <div class="request-response-boxes">
                    
                    @if (parsedMsg.requestRaw) {
                      <div class="payload-box request-box">
                        <div class="payload-title">Request Payload</div>
                        @if (parsedMsg.requestObj) {
                          <pre class="payload-pre"><code>{{ parsedMsg.requestObj | json }}</code></pre>
                        } @else {
                          <div class="payload-raw">{{ parsedMsg.requestRaw }}</div>
                        }
                      </div>
                    }

                    @if (parsedMsg.responseRaw) {
                      <div class="payload-box response-box">
                        <div class="payload-title">Service Response</div>
                        <div class="payload-raw response-raw">{{ parsedMsg.responseRaw }}</div>
                      </div>
                    }
                  </div>

                </div>
              } @else {
                <div class="message-box">
                  {{ selectedLog.errorMessage || '-' }}
                </div>
              }
            </div>
            <div class="detail-item full-width">
              <span class="detail-label">Error Details (Stack Trace)</span>
              <pre class="details-pre"><code>{{ selectedLog.errorDetails || 'No details available' }}</code></pre>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="modal-action-btn" (click)="closeDetails()">Close</button>
        </div>
      </div>
    </div>
  }
</div>
